import { useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { distinctUntilChanged } from 'rxjs/operators';
import { useStream } from '../../hooks/useStream';
import {
    ConfirmDialogModeEnum,
    DashboardTypeEnum,
    EditorModeEnum,
    GridConfig,
    RoleEnum,
    Stage,
    StageHeader,
    StageStatusEnum,
    StageViewModeEnum,
    Widget,
    WidgetData,
    WidgetTypeEnum,
} from '../../models/Index';
import Dashboard from '../../pages/dashboard/Dashboard';
import DoormanService from '../../services/doormanService/DoormanService';
import MigrationService from '../../services/migrationService/MigrationService';
import StageService from '../../services/stageService/StageService';
import StateService from '../../services/stateService/StateService';
import WidgetService from '../../services/widgetService/WidgetService';
import BlockUi from '../block-ui/BlockUi';
import AddWidgetButton from '../button-menu/AddWidgetButton';
import EmptyState from '../emptyState/EmptyState';
import Grid from '../grid/Grid';
import WidgetSelection from '../widgetSelection/WidgetSelection';

function Canvas() {
    const [stageHeader, setStageHeader] = useState([] as StageHeader[]);
    const [selectedHeader, setSelectedHeader] = useState({} as StageHeader);
    const [isLoading, setIsLoading] = useState(false);
    const [isLinkoutActive, setIsLinkoutActive] = useState(false);
    const [selectedStage, setSelectedStage] = useStream(StateService.selectedStage$);
    const [selectedWidgetData, setSelectedWidgetData] = useState({} as WidgetData);
    const [t] = useTranslation();
    const [widgets, setWidgets] = useStream(StateService.widgets$);
    const [widgetData, setWidgetData] = useStream(StateService.widgetData$);
    const [gridConfig, setGridConfig] = useState({} as GridConfig);
    const [confirmDialogMode, setConfirmDialogMode] = useState(ConfirmDialogModeEnum.CLOSED);
    const [currentCustomerUuid, setCurrentCustomerUuid] = useState('' as string);
    const [currentRoles, setCurrentRoles] = useState([] as RoleEnum[]);
    const [rolesPresent, setRolesPresent] = useState(false);
    const [savedStage, setSavedStage] = useState({} as Stage);
    const [stageViewMode, setStageViewMode] = useState(StageViewModeEnum.PUBLISHED as StageViewModeEnum);
    const [editorMode, setEditorMode] = useState(EditorModeEnum.CLOSED);
    const abortController = new AbortController();
    const theme = useTheme();
    const [height, setHeight] = useState('calc(100% - 50px)');
    const [isAddWidgetClicked, setIsAddWidgetClicked] = useState(false);
    const [isStatisticDashboard, setIsStatisticDashboard] = useState(false);

    useEffect(() => {
        const isViewer = [RoleEnum.VIEWER].includes(currentRoles[0]);
        setHeight(isViewer ? '99vh' : 'calc(100% - 50px)');
    }, [currentRoles]);

    useEffect(() => {
        const confirmDialogMode$ = StateService.confirmDialogMode$.subscribe((mode) => {
            setConfirmDialogMode(mode);
        });

        const currentRoles$ = StateService.currentRoles$.subscribe((currentRoles) => {
            setCurrentRoles(currentRoles);
        });

        const currentCustomerUuid$ = StateService.currentCustomerUuid$.subscribe((currentCustomerUuid) => {
            setCurrentCustomerUuid(currentCustomerUuid);
        });

        const editorMode$ = StateService.editorMode$.subscribe((editorMode) => {
            setEditorMode(editorMode);
        });

        const isLinkoutActive$ = StateService.isLinkoutActive$.subscribe((isLinkoutActive) => {
            setIsLinkoutActive(isLinkoutActive);
        });

        const savedStage$ = StateService.savedStage$.subscribe((savedStage) => {
            setSavedStage(savedStage);
        });

        const selectedHeader$ = StateService.selectedHeader$
            // Prevents re-load of stage on name change or re-click on current stage
            .pipe(
                distinctUntilChanged(
                    (prev: StageHeader, curr: StageHeader) => prev?.draftId === curr?.draftId && prev?.stageId === curr?.stageId,
                ),
            )
            .subscribe((header) => {
                setSelectedHeader(header);
            });

        const selectedWidgetData$ = StateService.selectedWidgetData$.subscribe((widget) => {
            setSelectedWidgetData(widget);
        });

        const stageHeader$ = StateService.stageHeader$.subscribe((array) => {
            const tempArray = StageService.sortHeaderArrayAlphabetically(array);
            setStageHeader(tempArray);
        });

        const stageViewMode$ = StateService.stageViewMode$.subscribe((mode) => {
            setStageViewMode(mode);
        });

        return () => {
            confirmDialogMode$.unsubscribe();
            currentRoles$.unsubscribe();
            currentCustomerUuid$.unsubscribe();
            editorMode$.unsubscribe();
            isLinkoutActive$.unsubscribe();
            savedStage$.unsubscribe();
            selectedHeader$.unsubscribe();
            selectedWidgetData$.unsubscribe();
            stageHeader$.unsubscribe();
            stageViewMode$.unsubscribe();
            abortController.abort();
        };
    }, []);
    useEffect(() => {
        if (selectedHeader?.stageId?.length > 0 && selectedHeader?.status !== StageStatusEnum.NEW) {
            setIsLoading(true);
            getStage(selectedHeader.stageId);
        } else if (selectedHeader?.status === StageStatusEnum.NEW) {
            const currentStage = StateService.selectedStage$.getValue();
            const newStage = {
                ...StageService.getNewStage(),
                dashboardType: currentStage?.dashboardType,
            };
            newStage.title = `${t('COMMON.NEW_DEFAULT_VALUE')}`;
            setSelectedStage(newStage);
            StateService.savedStage$.next(cloneDeep(newStage));
        } else if (!selectedHeader.draftId && !selectedHeader.stageId) {
            setGridConfig({} as GridConfig);
        }
    }, [selectedHeader, t]);

    useEffect(() => {
        // Skip widget data loading for statistical dashboards
        if (selectedStage?.dashboardType === DashboardTypeEnum.STATISTICS) {
            return;
        }

        let tempConfig = {} as GridConfig;
        let tempWidgetData = [] as WidgetData[];
        if (selectedStage?.publishedGridConfig && [StageViewModeEnum.PUBLISHED].includes(stageViewMode)) {
            tempConfig = selectedStage.publishedGridConfig;
            tempWidgetData = selectedStage.publishedWidgetData || ([] as WidgetData[]);
        }
        if (selectedStage?.workInProgressGridConfig && [StageViewModeEnum.WORK_IN_PROGRESS].includes(stageViewMode)) {
            tempConfig = selectedStage.workInProgressGridConfig;
            tempWidgetData =
                selectedStage.workInProgressWidgetData === ([] as WidgetData[])
                    ? selectedStage.publishedWidgetData
                    : selectedStage.workInProgressWidgetData;
        }
        setGridConfig(tempConfig);
        setWidgets(tempConfig?.layouts?.lg);
        setWidgetData(tempWidgetData);
    }, [selectedStage, stageViewMode]);

    useEffect(() => {
        if (confirmDialogMode === ConfirmDialogModeEnum.DELETE_STAGE_CONFIRMED) {
            removeStage();
        }
        if (confirmDialogMode === ConfirmDialogModeEnum.REMOVE_WIDGET_CONFIRMED) {
            WidgetService.removeWidget(selectedStage, selectedWidgetData);
        }
        if (confirmDialogMode === ConfirmDialogModeEnum.RESET_STAGE_TO_PUBLISH_CONFIRMED) {
            let tempStage = cloneDeep(selectedStage) as Stage;
            tempStage.workInProgressGridConfig = selectedStage.publishedGridConfig;
            tempStage.workInProgressWidgetData = selectedStage.publishedWidgetData;
            setSelectedStage(
                StageService.setGridLockState(
                    tempStage,
                    tempStage.workInProgressGridConfig?.layouts?.lg,
                    'workInProgressGridConfig',
                    false,
                ),
            );
            let header = stageHeader.filter((header) => header.stageId === tempStage.stageId)[0];
            [header, selectedHeader].forEach((item) => {
                if (item?.status) {
                    item.status = StageStatusEnum.PUBLISHED;
                }
            });
            StateService.stageHeader$.next(stageHeader);
            StateService.selectedHeader$.next(selectedHeader);
            StateService.confirmDialogMode$.next(ConfirmDialogModeEnum.CLOSED);
        }

        if (confirmDialogMode === ConfirmDialogModeEnum.RESET_STAGE_TO_SAVED_CONFIRMED) {
            let tempStage = cloneDeep(selectedStage) as Stage;
            tempStage.workInProgressGridConfig = savedStage.workInProgressGridConfig;
            tempStage.workInProgressWidgetData = savedStage.workInProgressWidgetData;
            setSelectedStage(tempStage);
            StateService.confirmDialogMode$.next(ConfirmDialogModeEnum.CLOSED);
        }
        // eslint-disable-next-line
    }, [confirmDialogMode]);

    useEffect(() => {
        setRolesPresent(currentRoles.length > 0 && currentRoles[0].length > 0 ? true : false);
    }, [currentRoles]);

    useEffect(() => {
        if (isLinkoutActive) {
            let canvas = document.getElementById('canvas');
            canvas.style.height = 'calc(100% - 8px)';
        }
    }, [isLinkoutActive]);

    useEffect(() => {
        const isStatistic = widgetData?.some((wD) => wD?.type === WidgetTypeEnum.SUPERSET_DASHBOARD);
        setIsStatisticDashboard(!!isStatistic);
    }, [widgetData]);

    async function getStage(stageId: string) {
        try {
            let stageResponse =
                (await StageService.getStage(stageId, currentCustomerUuid, { signal: abortController.signal })) ||
                StageService.getNewStage();

            prepareStage(stageResponse);
        } catch (error) {
            console.error('Error loading stage:', error);
            setIsLoading(false);
        }
    }

    async function removeStage() {
        if (selectedStage.stageId) {
            await deleteStage(selectedStage.stageId);
        } else {
            removeStageFromHeader();
        }
        StateService.confirmDialogMode$.next(ConfirmDialogModeEnum.CLOSED);
    }

    async function deleteStage(stageId: string) {
        setIsLoading(true);
        const response = await StageService.deleteStage(stageId, currentCustomerUuid, { signal: abortController.signal });
        setIsLoading(false);
        if (response === 200) {
            removeStageFromHeader();
        }
    }

    function prepareStage(stage: Stage) {
        if (stage.workInProgressGridConfig?.layouts?.lg?.length > 0) {
            stage = StageService.setGridLockState(stage, stage.workInProgressGridConfig?.layouts?.lg, 'workInProgressGridConfig', false);
        }
        if (stage?.publishedWidgetData?.length > 0 || stage?.widgetData?.length > 0) {
            stage = MigrationService.migrateStageData(stage) as Stage;
            stage.publishedWidgetData = cloneDeep(MigrationService.migrateWidgetData(stage.publishedWidgetData)) as WidgetData[];
            stage.workInProgressWidgetData = cloneDeep(MigrationService.migrateWidgetData(stage.workInProgressWidgetData)) as WidgetData[];
        }
        setIsLoading(false);
        setSelectedStage(stage);
        StateService.savedStage$.next(cloneDeep(stage));
    }

    function openSettings(value: WidgetData) {
        StateService.selectedWidgetData$.next(value);
        StateService.selectedWidget$.next(widgets.filter((item) => item.i === value.key)[0]);
        StateService.editorMode$.next(EditorModeEnum.WIDGET);
    }

    function removeStageFromHeader() {
        const index = stageHeader.indexOf(selectedHeader);
        let tempHeaderArray = stageHeader;
        tempHeaderArray.splice(index, 1);
        setStageHeader(tempHeaderArray);
        let newCurrentHeader = {} as StageHeader;
        if (tempHeaderArray.length > 0) {
            newCurrentHeader = index > 0 ? tempHeaderArray[index - 1] : tempHeaderArray[0];
        }
        StateService.stageHeader$.next(tempHeaderArray);
        StateService.selectedHeader$.next(newCurrentHeader);
        StateService.stageViewMode$.next(StageViewModeEnum.PUBLISHED);
        closeEditor();
    }

    function closeEditor() {
        setIsAddWidgetClicked(false);
        StateService.editorMode$.next(EditorModeEnum.CLOSED);
    }

    function removeWidget(value: WidgetData) {
        StateService.selectedWidgetData$.next(value);
        StateService.confirmDialogMode$.next(ConfirmDialogModeEnum.REMOVE_WIDGET);
    }

    function layoutChange(layout: Widget[]) {
        if (!isEqual(layout, widgets)) {
            setWidgets(layout);
        }
    }

    function addWidget(propChartType) {
        // Get current data
        const tempWidgets = cloneDeep(widgets) as Widget[];
        const tempWidgetData = cloneDeep(widgetData) as WidgetData[];
        const tempConfig = cloneDeep(gridConfig) as GridConfig;
        const tempSelectedStage = cloneDeep(selectedStage) as Stage;

        // Get new items
        const newWidget = WidgetService.getNewWidget(
            null,
            Number(widgets[widgets.length - 1]?.i || 0),
            widgets.length,
            selectedStage.granularityLevel,
            propChartType,
        );

        const newWidgetData = WidgetService.getInitialWidgetData();
        /* pre-set some of the data points */
        newWidgetData.title = t('COMMON.NEW_DEFAULT_VALUE');
        newWidgetData.key = newWidget.i.toString();
        newWidgetData.type = propChartType ? propChartType : WidgetTypeEnum.NOT_SELECTED;
        newWidgetData.isDescriptionHidden = true;

        // Process items
        tempWidgets.push(newWidget);
        tempWidgetData.push(newWidgetData);

        tempConfig.layouts.lg = tempWidgets;
        tempSelectedStage.workInProgressGridConfig = tempConfig;
        tempSelectedStage.workInProgressWidgetData = tempWidgetData;
        setIsAddWidgetClicked(false);

        // Update selected stage
        setSelectedStage(tempSelectedStage);
    }

    const handleAddWidgetClick = () => {
        setIsAddWidgetClicked(true);
        StateService.editorMode$.next(EditorModeEnum.CHART_TYPE);
    };

    return (
        <div
            id="canvas"
            data-testid="canvas"
            style={{
                overflowX: 'hidden',
                overflowY: 'auto',
                /*marginRight: '32px',*/
                height: height,
            }}
        >
            {isLoading && <BlockUi />}
            {!isLoading && (
                <>
                    {gridConfig?.layouts?.lg?.length > 0 ? (
                        <Grid gridConfig={gridConfig} widgetData={widgetData} openSettings={openSettings} removeWidget={removeWidget} />
                    ) : (
                        <EmptyState addWidget={handleAddWidgetClick} roles={currentRoles} stageViewMode={stageViewMode} />
                    )}
                    {stageViewMode === StageViewModeEnum.WORK_IN_PROGRESS &&
                        !isLinkoutActive &&
                        rolesPresent &&
                        !isStatisticDashboard &&
                        DoormanService.isAccessGranted('addWidgetButton', currentRoles) && (
                            <>
                                <div
                                    style={{
                                        position: 'absolute',
                                        bottom: theme.spacing(6),
                                        right: theme.spacing(1),
                                    }}
                                >
                                    <AddWidgetButton addWidget={handleAddWidgetClick} />
                                </div>
                                {isAddWidgetClicked && <WidgetSelection addWidgetFunc={addWidget} closeEditor={closeEditor} />}
                            </>
                        )}
                </>
            )}
        </div>
    );
}

export default Canvas;
